<template>
  <Teleport to="body">
    <div v-if="modelValue" class="ph-cmd-overlay" @click="close">
      <div class="ph-cmd" @click.stop>
        <header class="ph-cmd__header">
          <PhIcon name="search" :size="16" class="ph-cmd__search-icon" />
          <input
            ref="inputRef"
            v-model="query"
            :placeholder="$t('components.searchPages')"
            class="ph-cmd__input"
            @keydown.down.prevent="moveSelection(1)"
            @keydown.up.prevent="moveSelection(-1)"
            @keydown.enter.prevent="commitSelection"
            @keydown.esc="close"
          />
          <span class="ph-cmd__shortcut num">ESC</span>
        </header>
        <div class="ph-cmd__body">
          <div class="ph-cmd__section-title">{{ $t('components.pages') }}</div>
          <div v-if="results.length === 0" class="ph-cmd__empty">{{ $t('components.noMatches') }}</div>
          <button
            v-for="(r, i) in results"
            :key="r.id"
            class="ph-cmd__item"
            :class="{ 'ph-cmd__item--active': i === idx }"
            @mouseenter="idx = i"
            @click="navigate(r.id)"
          >
            <PhIcon :name="r.icon || 'reports'" :size="14" />
            <span class="ph-cmd__item-label">{{ r.label }}</span>
            <PhIcon v-if="i === idx" name="chevron" :size="12" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import PhIcon from './PhIcon.vue'

const props = defineProps<{
  modelValue: boolean
  pages: { id: string; label: string; icon?: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'navigate', id: string): void
}>()

const query = ref('')
const idx = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

const results = computed(() =>
  props.pages.filter((p) => p.label.toLowerCase().includes(query.value.toLowerCase())).slice(0, 8),
)

watch(query, () => {
  idx.value = 0
})

watch(
  () => props.modelValue,
  async (v) => {
    if (v) {
      query.value = ''
      idx.value = 0
      await nextTick()
      inputRef.value?.focus()
    }
  },
)

function close() {
  emit('update:modelValue', false)
}

function moveSelection(delta: number) {
  if (delta > 0) idx.value = Math.min(results.value.length - 1, idx.value + 1)
  else idx.value = Math.max(0, idx.value - 1)
}

function commitSelection() {
  const r = results.value[idx.value]
  if (r) navigate(r.id)
}

function navigate(id: string) {
  emit('navigate', id)
  close()
}
</script>

<style scoped>
.ph-cmd-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 14vh;
  animation: fadeIn 120ms ease;
}
.ph-cmd {
  width: min(560px, calc(100vw - 32px));
  background: var(--ph-surface);
  border: 1px solid var(--ph-border);
  border-radius: 16px;
  box-shadow: var(--ph-shadow-lg);
  overflow: hidden;
}
.ph-cmd__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--ph-border);
}
.ph-cmd__search-icon {
  color: var(--ph-text-3);
}
.ph-cmd__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--ph-text);
  font-size: 15px;
  letter-spacing: -0.2px;
  font-family: 'Geist', sans-serif;
}
.ph-cmd__input::placeholder {
  color: var(--ph-text-3);
}
.ph-cmd__shortcut {
  font-size: 11px;
  font-weight: 600;
  color: var(--ph-text-3);
  padding: 2px 6px;
  background: var(--ph-chip-bg);
  border: 1px solid var(--ph-border);
  border-radius: 5px;
}
.ph-cmd__body {
  padding: 6px;
  max-height: 360px;
  overflow: auto;
}
.ph-cmd__section-title {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ph-text-3);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  padding: 8px 10px 4px;
}
.ph-cmd__empty {
  padding: 24px;
  text-align: center;
  color: var(--ph-text-3);
  font-size: 13px;
}
.ph-cmd__item {
  width: 100%;
  padding: 9px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ph-text);
  font-size: 13.5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  font-family: 'Geist', sans-serif;
}
.ph-cmd__item--active {
  background: var(--ph-chip-bg);
}
.ph-cmd__item-label {
  flex: 1;
}
</style>
